"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const productFamilies = [{
  id: 1,
  title: "Phenolic Legend Plates",
  url: "/Contact",
  description: "Engraved pushbutton and operator legend plates — 22mm, 30mm, rectangular, and emergency-stop variants in phenolic and acrylic.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Phenolic_Legend_Plates-scaled-e1781520243456.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Phenolic_Legend_Plates-scaled-e1781520243456.png",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Phenolic_Legend_Plates-scaled-e1781520243456.png",
  imageAlt: "Phenolic Legend Plates"
}, {
  id: 2,
  title: "Engraved Nameplates",
  url: "/Contact",
  description: "Panel, equipment, valve, and asset nameplates across phenolic, stainless, aluminum, and brass.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraved_nameplates.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraved_nameplates.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraved_nameplates.png-wvc-sizes",
  imageAlt: "Engraved Nameplates"
}, {
  id: 3,
  title: "Arc Flash Labels",
  url: "/products/arc-flash-labels/",
  description: "Ready-to-order arc flash labels in engraved phenolic or aluminum, produced from your study data.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels.png-wvc-sizes",
  imageAlt: "Arc Flash Labels"
}, {
  id: 4,
  title: "Electrical Panel Schedules",
  url: "/products/electrical-panel-schedules/",
  description: "Engraved panel directory and schedule plates for permanent breaker identification.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/electrical_panel_schedules.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/electrical_panel_schedules.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/electrical_panel_schedules.png-wvc-sizes",
  imageAlt: "Electrical Panel Schedules"
}, {
  id: 5,
  title: "Cable & Wire ID Tags",
  url: "/products/cable-wire-id-tags/",
  description: "Stainless steel, phenolic, and brass cable and wire identification tags for permanent circuit ID.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cable_wire_id_tags.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cable_wire_id_tags.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cable_wire_id_tags.png-wvc-sizes",
  imageAlt: "Cable & Wire ID Tags"
}, {
  id: 6,
  title: "Pipe & Conduit Markers",
  url: "/products/pipe-conduit-markers/",
  description: "ANSI/ASME A13.1 pipe markers and conduit identification for facility-wide systems.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pipe_conduit_markers.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pipe_conduit_markers.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pipe_conduit_markers.png-wvc-sizes",
  imageAlt: "Pipe & Conduit Markers"
}, {
  id: 7,
  title: "Lockout / Tagout Tags",
  url: "/products/lockout-tagout-tags/",
  description: "LOTO tags produced to support OSHA 1910.147 energy-control programs.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/lockout_tagout_tags.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/lockout_tagout_tags.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/lockout_tagout_tags.png-wvc-sizes",
  imageAlt: "Lockout / Tagout Tags"
}, {
  id: 8,
  title: "Pushbutton Legend Plates",
  url: "/products/pushbutton-legend-plates/",
  description: "Operator-interface legend plates for industrial control panels in standard control sizes.",
  ctaText: "Configure & quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pushbutton_legend_plates.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pushbutton_legend_plates.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pushbutton_legend_plates.png-wvc-sizes",
  imageAlt: "Pushbutton Legend Plates"
}, {
  id: 9,
  title: "MIL-SPEC Phenolic",
  url: "/products/mil-spec-phenolic/",
  description: "MIL-P-15035 / MIL-DTL-15024 phenolic nameplates for defense, marine, and aerospace programs.",
  ctaText: "Request a quote",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mil_spec_phenolic.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mil_spec_phenolic.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mil_spec_phenolic.png-wvc-sizes",
  imageAlt: "MIL-SPEC Phenolic"
}];
function AnimatedCard({
  family,
  index
}: {
  family: (typeof productFamilies)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const CardWrapper = index === 0 || index === 1 || index === 2 || index === 3 || index === 4 || index === 5 || index === 6 || index === 7 || index === 8 ? "div" : Link;
  const wrapperProps = index === 0 || index === 1 || index === 2 || index === 3 || index === 4 || index === 5 || index === 6 || index === 7 || index === 8 ? {} : {
    to: family.url
  };
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <CardWrapper {...wrapperProps as any} className="block h-full group outline-none">
        <Card className="h-full bg-secondary border-2 border-transparent group-hover:border-primary group-focus-visible:border-primary transition-colors duration-300 overflow-hidden shadow-md flex flex-col">
          <AspectRatio ratio={16 / 9} className="bg-muted overflow-hidden">
            <img src={family.imageSrc} data-wvc-srcset={family.imageSrcSet} data-wvc-sizes={family.imageSizes} alt={family.imageAlt} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          </AspectRatio>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-default text-foreground group-hover:text-primary transition-colors">
              {family.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4 flex-grow">
            <CardDescription className="text-base font-default text-muted-foreground leading-relaxed">
              {family.description}
            </CardDescription>
          </CardContent>
        </Card>
      </CardWrapper>
    </div>;
}
export default function ProductFamilyGrid() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="product-family-grid" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6">
            Which Engraved Product Do You Need?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Pick a product family to configure and quote, or upload a file for a
            custom item Evermark routes to the right material and process.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {productFamilies.map((family, i) => <AnimatedCard key={family.id} family={family} index={i} />)}
        </div>
      </div>
    </section>;
}